import {
  type Theme,
  type TouchlessTicket,
  type UserProfile,
} from '@parkingbase/ecommerce-api-test';

const DISCOUNT_PROGRAM_ROLES = ['USER', 'ADMIN', 'SUPER_ADMIN'];

/** Minimum length a rateCardId must exceed to be treated as a real id (matches legacy `> 5`). */
const MIN_RATE_CARD_ID_LENGTH = 5;

/**
 * Resolves the special-rate id persisted on a touchless ticket, so extend/overdue
 * payments re-send the coupon/special rate that was applied at booking.
 *
 * Mirrors the legacy reservation site's `PaymentForm.getSpecialRateId()`: a
 * SPECIALRATE discount-program ticket carries the id on `rateOutput.rateCardId`,
 * while a coupon-applied ticket carries it on `ticket.specialRateId`.
 *
 * ## Invariants:
 * - Must be derived from the persisted ticket, NOT from transient in-memory
 *   applied-coupon state. On a fresh touchless page load the in-memory coupon is
 *   absent, so reading it from memory sends an empty `specialRateId`, which makes
 *   the backend drop the coupon on re-rate — leaving a false balance, a shrunken
 *   paid-until, and no End Parking (PB-3052).
 *
 * @returns the special-rate id, or `undefined` when the ticket carries none.
 */
export function resolveTicketSpecialRateId(
  ticket: TouchlessTicket | null | undefined
): string | undefined {
  const rateOutput = ticket?.rateOutput;
  if (
    ticket?.discountProgram?.documentId &&
    rateOutput?.rateType === 'SPECIALRATE' &&
    (rateOutput?.rateCardId?.length ?? 0) > MIN_RATE_CARD_ID_LENGTH
  ) {
    return rateOutput.rateCardId;
  }
  return ticket?.ticket?.specialRateId || undefined;
}

/**
 * Checks whether the user has special rate program access or discount program roles
 * for the current company. Mirrors the legacy app's `isSpecialRateProgramOrRolePresent`.
 *
 * Used to gate access to the "Search by ticket number / plate" feature.
 */
export function hasSpecialRateAccess(
  user: UserProfile | null | undefined,
  theme: Theme | null | undefined
): boolean {
  if (!user || !theme?.companyId) return false;

  const companyId = theme.companyId;

  // Check if user has discount program roles (ADMIN, SUPER_ADMIN, USER) for this company
  const programRoles = user.discountProgramRoles?.[companyId];
  if (programRoles) {
    const hasRoleAccess = Object.values(programRoles).some((roles) =>
      (roles as string[]).some((role) => DISCOUNT_PROGRAM_ROLES.includes(role))
    );
    if (hasRoleAccess) return true;
  }

  // Check if user has special rate programs for this company
  const specialPrograms = user.specialRateProgramMap?.[companyId];
  if (specialPrograms && specialPrograms.length > 0) return true;

  return false;
}

// Mirrors legacy isTouchlessQR() (HomePage.tsx:381-388): on a converge site the
// site-level flag is ignored and the participants' flags are ORed instead.
export function isTouchlessQrEnabled(
  theme: Theme | null | undefined,
  companyThemes?: Record<string, Theme> | null
): boolean {
  if (!theme) return false;
  if (theme.isConvergeWebsite) {
    return Object.values(companyThemes ?? {}).some((t) => !!t?.touchlessQrScanner);
  }
  return !!theme.touchlessQrScanner;
}

// Gates the scan entry points, mirroring legacy HomePage.tsx:1509. The OR is
// deliberate: a validator still needs ticket lookup when the company has
// touchless QR off. Not for the find-session bar — that advertises plate/ticket
// lookup, so it gates on hasSpecialRateAccess alone.
export function isScanEntryVisible(
  user: UserProfile | null | undefined,
  theme: Theme | null | undefined,
  companyThemes?: Record<string, Theme> | null
): boolean {
  return isTouchlessQrEnabled(theme, companyThemes) || hasSpecialRateAccess(user, theme);
}
